import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  enableIndexedDbPersistence,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '@/firebase';
import { SearchConsoleConnection, Project, DailyAnalytics, SearchConsoleProperty, ProjectDetails } from '../types/firestore';

// Enable offline persistence
enableIndexedDbPersistence(db).catch((err) => {
  console.error('Firestore persistence error:', err);
});

export class FirestoreService {
  private static instance: FirestoreService;
  private projectCache: Map<string, Project> = new Map();
  private analyticsCache: Map<string, DailyAnalytics> = new Map();

  private constructor() {}

  static getInstance(): FirestoreService {
    if (!FirestoreService.instance) {
      FirestoreService.instance = new FirestoreService();
    }
    return FirestoreService.instance;
  }

  // Search Console Connections
  async getSearchConsoleConnection(userId: string) {
    const docRef = doc(db, `users/${userId}/search-console/connections`);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() as SearchConsoleConnection : null;
  }

  async updateSearchConsoleConnection(userId: string, data: Partial<SearchConsoleConnection>) {
    const docRef = doc(db, `users/${userId}/search-console/connections`);
    await setDoc(docRef, data, { merge: true });
  }

  // Projects
  async getProject(userId: string, projectId: string): Promise<Project | null> {
    // Check cache first
    const cacheKey = `${userId}:${projectId}`;
    if (this.projectCache.has(cacheKey)) {
      return this.projectCache.get(cacheKey) || null;
    }

    const docRef = doc(db, `users/${userId}/projects/${projectId}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const project = docSnap.data() as Project;
      this.projectCache.set(cacheKey, project);
      return project;
    }
    return null;
  }

  async createProject(userId: string, projectId: string, data: Omit<Project, 'createdAt' | 'updatedAt'>) {
    const now = Timestamp.now();
    const project: Project = {
      ...data,
      createdAt: now,
      updatedAt: now
    };

    const docRef = doc(db, `users/${userId}/search-console/projects/${projectId}`);
    await setDoc(docRef, project);
    
    // Update cache
    this.projectCache.set(`${userId}:${projectId}`, project);
    return project;
  }

  async updateProject(userId: string, projectId: string, data: Partial<Project>) {
    const docRef = doc(db, `users/${userId}/search-console/projects/${projectId}`);
    const updateData = {
      ...data,
      updatedAt: Timestamp.now()
    };
    
    await updateDoc(docRef, updateData);
    
    // Update cache
    const cacheKey = `${userId}:${projectId}`;
    const existingProject = this.projectCache.get(cacheKey);
    if (existingProject) {
      this.projectCache.set(cacheKey, { ...existingProject, ...updateData });
    }
  }

  async createSearchConsoleProject(
    userId: string,
    projectData: { name: string; url: string }
  ): Promise<Project> {
    console.log('Creating search console project:', { userId, projectData });
    
    // Input validation
    if (!userId?.trim()) throw new Error('User ID is required');
    if (!projectData.name?.trim()) throw new Error('Project name is required');
    if (!projectData.url?.trim()) throw new Error('Project URL is required');

    try {
      // Create a new document reference to get auto-generated ID
      const projectsRef = collection(db, `users/${userId}/projects`);
      console.log('Collection reference created:', projectsRef.path);
      
      const newProjectRef = doc(projectsRef);
      console.log('New project reference created:', newProjectRef.path);

      const now = Timestamp.now();
      const project: Project = {
        name: projectData.name.trim(),
        url: projectData.url.trim(),
        createdAt: now,
        updatedAt: now,
        settings: {
          crawlFrequency: 'daily',
          enableAiAnalysis: false
        }
      };
      console.log('Project data prepared:', project);

      // Store the project in Firestore
      await setDoc(newProjectRef, project);
      console.log('Project stored in Firestore');

      // Update cache
      this.projectCache.set(`${userId}:${newProjectRef.id}`, project);
      console.log('Project cache updated');

      return project;
    } catch (error) {
      console.error('Error in createSearchConsoleProject:', error);
      throw new Error('Failed to create search console project: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async getAllProjects(userId: string): Promise<Project[]> {
    try {
      const projectsRef = collection(db, `users/${userId}/projects`);
      const projectsSnap = await getDocs(projectsRef);
      
      const projects = projectsSnap.docs.map(doc => {
        const data = doc.data() as Project;
        // Update cache
        this.projectCache.set(`${userId}:${doc.id}`, data);
        return data;
      });

      return projects;
    } catch (error) {
      console.error('Error fetching projects:', error);
      throw new Error('Failed to fetch projects');
    }
  }

  async deleteProject(userId: string, projectId: string): Promise<void> {
    try {
      // Input validation
      if (!userId?.trim()) throw new Error('User ID is required');
      if (!projectId?.trim()) throw new Error('Project ID is required');

      const projectRef = doc(db, `users/${userId}/projects/${projectId}`);
      
      // Check if project exists before deleting
      const projectDoc = await getDoc(projectRef);
      if (!projectDoc.exists()) {
        throw new Error('Project not found');
      }

      // Delete the project document
      await deleteDoc(projectRef);

      // Remove from cache
      this.projectCache.delete(`${userId}:${projectId}`);

      console.log(`Project ${projectId} deleted successfully`);
    } catch (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  async updateProjectDetails(
    userId: string, 
    projectUrl: string, 
    details: ProjectDetails
  ): Promise<void> {
    try {
      // Find the project document
      const projectsRef = collection(db, `users/${userId}/projects`);
      const q = query(projectsRef, where('url', '==', projectUrl));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        throw new Error('Project not found');
      }

      // Get the first matching project document
      const projectDoc = querySnapshot.docs[0];
      const projectRef = projectDoc.ref;

      // Update the project with new details
      await updateDoc(projectRef, {
        details: {
          ...details,
          lastUpdated: Timestamp.now()
        }
      });

      console.log('Project details updated successfully');
    } catch (error) {
      console.error('Error updating project details:', error);
      throw new Error('Failed to update project details');
    }
  }

  async getProjectDetailsByUrl(
    userId: string, 
    projectUrl: string
  ): Promise<Project | null> {
    try {
      const projectsRef = collection(db, `users/${userId}/projects`);
      const q = query(projectsRef, where('url', '==', projectUrl));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }

      // Get the first matching project document
      const projectDoc = querySnapshot.docs[0];
      const projectData = projectDoc.data() as Project;
      
      // Include the document ID
      return {
        ...projectData,
        id: projectDoc.id
      };
    } catch (error) {
      console.error('Error fetching project details:', error);
      throw new Error('Failed to fetch project details');
    }
  }

  // Analytics
  async getDailyAnalytics(userId: string, projectId: string, date: string): Promise<DailyAnalytics | null> {
    const cacheKey = `${userId}:${projectId}:${date}`;
    
    // Check cache first for recent analytics
    if (this.analyticsCache.has(cacheKey)) {
      return this.analyticsCache.get(cacheKey) || null;
    }

    const docRef = doc(db, `users/${userId}/search-console/analytics/${projectId}/${date}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const analytics = docSnap.data() as DailyAnalytics;
      // Only cache recent analytics (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      if (new Date(date) >= sevenDaysAgo) {
        this.analyticsCache.set(cacheKey, analytics);
      }
      return analytics;
    }
    return null;
  }

  async updateDailyAnalytics(userId: string, projectId: string, date: string, data: DailyAnalytics) {
    const docRef = doc(db, `users/${userId}/search-console/analytics/${projectId}/${date}`);
    await setDoc(docRef, data, { merge: true });
    
    // Update cache
    const cacheKey = `${userId}:${projectId}:${date}`;
    this.analyticsCache.set(cacheKey, data);
  }

  // Search Console Properties
  async addSearchConsoleProperty(
    userId: string, 
    property: SearchConsoleProperty
  ): Promise<void> {
    if (!userId?.trim()) throw new Error('User ID is required');
    if (!property.propertyId?.trim()) throw new Error('Property ID is required');
    if (!property.siteUrl?.trim()) throw new Error('Site URL is required');
    
    try {
      // Ensure propertyId is safe for use as a document ID
      const safePropertyId = property.propertyId.replace(/[^\w-]/g, '-');
      
      // Create reference following the collection/document alternating pattern
      const propertyRef = doc(
        collection(
          doc(
            collection(
              doc(
                collection(db, 'users'),
                userId
              ),
              'search-console'
            ),
            'properties'
          ),
          safePropertyId
        ),
        'data'
      );
      
      const now = Timestamp.now();
      await setDoc(propertyRef, {
        ...property,
        propertyId: safePropertyId, // Store the safe ID
        createdAt: now,
        updatedAt: now,
        lastSyncDate: now,
        siteUrl: property.siteUrl // Ensure the method uses the new siteUrl property
      });
    } catch (error) {
      console.error('Error adding search console property:', error);
      throw new Error('Failed to add search console property');
    }
  }

  async getSearchConsoleProperties(userId: string): Promise<SearchConsoleProperty[]> {
    try {
      const propertiesRef = collection(db, `users/${userId}/search-console/properties`);
      const propertiesSnap = await getDocs(propertiesRef);
      
      return propertiesSnap.docs.map(doc => doc.data() as SearchConsoleProperty);
    } catch (error) {
      console.error('Error fetching search console properties:', error);
      throw new Error('Failed to fetch search console properties');
    }
  }

  async updateSearchConsoleProperty(
    userId: string,
    propertyId: string,
    updates: Partial<SearchConsoleProperty>
  ): Promise<void> {
    try {
      const propertyRef = doc(db, `users/${userId}/search-console/properties/${propertyId}`);
      await updateDoc(propertyRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating search console property:', error);
      throw new Error('Failed to update search console property');
    }
  }

  // Cache management
  clearCache() {
    this.projectCache.clear();
    this.analyticsCache.clear();
  }
}
